import React, { useMemo, useState } from 'react';

import _ from 'lodash';

import { Checkbox } from 'src/app/components/lib/checkbox';
import { ControlBar, ControlGroup, ControlSearchBox } from 'src/app/components/lib/control-bar';
import { Modal } from 'src/app/components/lib/modal';
import { Radio, RadioGroup } from 'src/app/components/lib/radio';
import { GridLinkOperator, ModalDataGrid } from 'src/app/components/lib/table';
import { FilterType } from 'src/app/components/lib/wizard/filters';

import { FILTER_RESOURCES } from './constants';
import { useConditionalFilterModalParameters } from './use-conditional-filter-modal-parameters';
import { useFilterResourceData } from './use-filter-resource-data';
import { useFilteredProducts } from './use-filtered-products';
import { useProductListColumns } from './use-product-list-columns';

import type { FilterResourceType } from './filter';
import type { ProductActiveFilterResources } from './use-filter-resource-data';
import type { GridFilterItem } from 'src/app/components/lib/table';

type FilterModalProps = {
  filterResourceType: FilterResourceType;
  requirement: number | 'reward';
};

enum FilterResourceToProductKey {
  brand = 'BrandId',
  category = 'CategoryId',
  'inventory tag' = 'InventoryTagId',
  'pricing tier' = 'PricingTierId',
  product = 'ProductId',
  'product tag' = 'ProductTagId',
  strain = 'StrainId',
  vendor = 'VendorId',
  weight = 'Weight',
}

function isProductResource(
  _resource: unknown,
  filterResourceType: FilterResourceType
): _resource is ProductActiveFilterResources {
  return filterResourceType === FILTER_RESOURCES.Product;
}

export function FilterModal({ filterResourceType, requirement }: FilterModalProps) {
  // conditionals for requirement products filters versus reward products filters
  const { filters, noun, onClose, titleFragment, updateFilter } = useConditionalFilterModalParameters(requirement);
  const filteredProductsFromResourceFilters = useFilteredProducts({ requirement });

  // data
  const { activeFilterResources, loading } = useFilterResourceData({ filterResourceType });

  // state
  const [selectedIds, setSelectedIds] = useState<number[]>(
    filters[filterResourceType].resources.map((resource) => resource.resourceId)
  );
  const [selectionType, setSelectionType] = useState(
    filters[filterResourceType].selectionType === FilterType.NONE
      ? FilterType.INCLUSIONARY
      : filters[filterResourceType].selectionType
  );
  const [searchString, setSearchString] = useState('');
  const [shouldStackOtherFilters, setShouldStackOtherFilters] = useState(false);

  //  data grid filtering data
  const filteredActiveFilterResourceIds = useMemo(() => {
    // get all the unique values of the filterResourceType from the filteredProductsFromResourceFilters
    const filteredProductsUniqueByFilterResourceType = _.uniqBy(
      filteredProductsFromResourceFilters,
      FilterResourceToProductKey[filterResourceType]
    );
    // get all resourceIds from the filteredProductsUniqueByFilterResourceType list
    return filteredProductsUniqueByFilterResourceType.map(
      (product) => product[FilterResourceToProductKey[filterResourceType]]?.toString() ?? ''
    );
  }, [filterResourceType, filteredProductsFromResourceFilters]);

  const filterModelItems: GridFilterItem[] = useMemo(() => {
    const filterModelItemsToUse: GridFilterItem[] = [];

    if (shouldStackOtherFilters) {
      filterModelItemsToUse.push({
        id: 3,
        columnField: filterResourceType === FILTER_RESOURCES.Product ? 'ProductId' : 'resourceId',
        operatorValue: 'isAnyOf',
        value: filteredActiveFilterResourceIds,
      });
    }
    return filterModelItemsToUse;
  }, [filterResourceType, filteredActiveFilterResourceIds, shouldStackOtherFilters]);

  const filterResourcePluralNoun =
    filterResourceType === FILTER_RESOURCES.Category ? 'categories' : `${filterResourceType}s`;

  const productColumns = useProductListColumns();
  const getResourceColumns = () => {
    switch (filterResourceType) {
      case FILTER_RESOURCES.Product:
        return productColumns;
      case FILTER_RESOURCES.Category:
        return [
          { headerName: 'Category', field: 'resourceName' },
          { headerName: 'Master category', field: 'masterCategory' },
          { headerName: 'Total products', field: 'resourceNumberOfProducts' },
          { headerName: 'ID', field: 'resourceId', hide: true },
        ];
      default:
        return [
          { headerName: filterResourceType, field: 'resourceName' },
          { headerName: 'Total products', field: 'resourceNumberOfProducts' },
          { headerName: 'ID', field: 'resourceId', hide: true },
        ];
    }
  };

  const resourceColumns = getResourceColumns();

  // handlers
  const onSave = () => {
    const selectedResources = activeFilterResources.filter((resource) =>
      selectedIds.some((id) => id === resource.resourceId)
    );
    const newSelectionType = selectedResources.length === 0 ? FilterType.NONE : selectionType;
    updateFilter({ resources: selectedResources, resourceType: filterResourceType, selectionType: newSelectionType });
  };

  // sorted data grid rows
  const sortedActiveFilterResources = useMemo(() => {
    const cloneActiveFilterResources = _.cloneDeep(activeFilterResources);
    // sort the activeFilterResources array by having objects that have a resourceId included in the selectedIds array first
    cloneActiveFilterResources.sort((a, b) => {
      const aIsSelected = selectedIds.includes(a.resourceId);
      const bIsSelected = selectedIds.includes(b.resourceId);
      const bEvaluated = bIsSelected ? 1 : 0;
      return aIsSelected ? -1 : bEvaluated;
    });
    return cloneActiveFilterResources;
  }, [activeFilterResources, selectedIds]);

  const filteredActiveFilterResources = useMemo(
    () =>
      sortedActiveFilterResources.filter((resource) => {
        const nameMatchesSearchTerm = resource.resourceName.toLowerCase().includes(searchString.toLowerCase());

        if (isProductResource(resource, filterResourceType)) {
          const productSkuMatchesSearchTerm = resource.ProductNo?.toLowerCase().includes(searchString.toLowerCase());
          return nameMatchesSearchTerm || productSkuMatchesSearchTerm;
        }

        return nameMatchesSearchTerm;
      }),
    [sortedActiveFilterResources, searchString, filterResourceType]
  );

  return (
    <Modal
      Content={
        <div>
          <RadioGroup
            label='Selection type:'
            tooltip={`Restrict ${noun} to only products matching the selected ${filterResourcePluralNoun}, or allow all products with the exception of products matching the selected ${filterResourcePluralNoun}.`}
            onChange={({ target }) => setSelectionType(target.value)}
          >
            <Radio
              checked={selectionType === FilterType.INCLUSIONARY}
              label='Only include'
              value={FilterType.INCLUSIONARY}
            />
            <Radio
              checked={selectionType === FilterType.EXCLUSIONARY}
              label='Exclude'
              value={FilterType.EXCLUSIONARY}
            />
          </RadioGroup>
          <ControlBar margin='var(--sizes-30) 0 var(--sizes-50)'>
            <ControlGroup grow>
              <ControlSearchBox placeholder={`Search ${filterResourcePluralNoun} by name`} onChange={setSearchString} />
              <Checkbox
                checked={shouldStackOtherFilters}
                label={
                  <>
                    Hide options with no <br /> eligible products
                  </>
                }
                onChange={() => setShouldStackOtherFilters(!shouldStackOtherFilters)}
              />
            </ControlGroup>
          </ControlBar>
          <ModalDataGrid
            checkboxSelection
            columns={resourceColumns}
            filterModel={{
              items: filterModelItems,
              linkOperator: GridLinkOperator.Or,
            }}
            getRowId={(row) => row.resourceId}
            hideActionsColumn={false}
            loading={loading}
            name={`filter-product-by-${filterResourceType}-product-list`}
            rows={filteredActiveFilterResources}
            selectionModel={selectedIds.filter((id) =>
              filteredActiveFilterResources.some((resource) => resource.resourceId === id)
            )}
            onSelectionModelChange={(newModel) => {
              // Preserve existing selections that aren't currently visible due to filtering
              const newSelectedIds = newModel as number[];
              const visibleResourceIds = filteredActiveFilterResources.map((resource) => resource.resourceId);
              const hiddenSelections = selectedIds.filter((id) => !visibleResourceIds.includes(id));
              setSelectedIds([...newSelectedIds, ...hiddenSelections]);
            }}
          />
        </div>
      }
      isExpandable
      open
      primaryActions={[
        {
          label: 'Cancel',
          onClick: onClose,
          variant: 'secondary',
        },
        {
          label: 'Save',
          onClick: onSave,
        },
      ]}
      title={`Filter ${titleFragment} by ${filterResourceType}`}
      width='720px'
      onClose={onClose}
    />
  );
}
